import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiMessages, readApiStatus } from "@/shared/utils/apiError";

/**
 * What a failed adjust means, decided from the status code and — for the 400s — from the backend's own
 * English literals.
 *
 * Matching on literals is not a shortcut, it is forced: of the six things this endpoint can refuse, only the
 * 409 goes through i18n (`errors.wallet.adjustment_reference_already_used`). The rest are hardcoded English
 * strings in `card-wallets.service.ts`, and one of them names the card provider, which rule 12 forbids on
 * screen. So the sentence is never rendered — it is only read to pick which Spanish sentence of ours to
 * show.
 */
export type AdjustErrorKind =
  | "amount_zero"
  | "currency_mismatch"
  | "ineligible_wallet"
  | "insufficient_funds"
  | "wallet_not_found"
  | "duplicate_reference"
  | "rate_limited"
  | "no_permission"
  | "unconfirmed"
  | "generic";

export interface ClassifiedAdjustError {
  kind: AdjustErrorKind;
  status: number | null;
  /** The backend sentence. Rendered only for `generic`; every other kind gets our own copy. */
  message: string;
  /** Seconds to wait before the throttle window reopens. Only meaningful for `rate_limited`. */
  retryAfterSeconds: number;
}

/** `@Throttle({ default: { limit: 1, ttl: 60000 } })` — the window when the header does not say. */
export const ADJUST_THROTTLE_SECONDS = 60;

function retryAfterFrom(error: unknown): number {
  if (!HttpApiService.isAxiosError(error)) return ADJUST_THROTTLE_SECONDS;
  const raw = error.response?.headers?.["retry-after"];
  const seconds = Number(Array.isArray(raw) ? raw[0] : raw);
  if (!Number.isFinite(seconds) || seconds <= 0) return ADJUST_THROTTLE_SECONDS;
  return Math.min(Math.ceil(seconds), ADJUST_THROTTLE_SECONDS);
}

function matches(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

export function classifyAdjustError(error: unknown): ClassifiedAdjustError {
  const status = readApiStatus(error);
  const message = readApiMessages(error).join(" ");
  const base = { status, message, retryAfterSeconds: ADJUST_THROTTLE_SECONDS };

  // No answer, a gateway failure or a timeout: the request may well have been executed. Never an error the
  // operator can "retry" — the reference has to be looked up first.
  if (status === null || status >= 500 || status === 408) return { ...base, kind: "unconfirmed" };

  if (status === 429) return { ...base, kind: "rate_limited", retryAfterSeconds: retryAfterFrom(error) };

  if (status === 409) {
    // Two different 409s share this status. The idempotency interceptor's ones name the header and mean a
    // request with this key is in flight (or was replayed with a different body) — both ambiguous. The
    // domain one is the reference, and it is the only message the backend localizes.
    return matches(message, "Idempotency-Key")
      ? { ...base, kind: "unconfirmed" }
      : { ...base, kind: "duplicate_reference" };
  }

  if (status === 404) return { ...base, kind: "wallet_not_found" };

  // A missing RBAC permission answers 401, not 403 (`jwt-permissions.guard.ts`) — and `HttpApiService` has
  // already tried a refresh-and-replay by the time it lands here, so the two are indistinguishable.
  if (status === 401 || status === 403) return { ...base, kind: "no_permission" };

  if (status === 400) {
    if (matches(message, "Amount must be different from 0")) return { ...base, kind: "amount_zero" };
    if (matches(message, "Currencies do not match")) return { ...base, kind: "currency_mismatch" };
    if (matches(message, "Manual adjustment allowed only")) return { ...base, kind: "ineligible_wallet" };
    if (matches(message, "Insufficient funds")) return { ...base, kind: "insufficient_funds" };
    // `increaseBalanceAtomically` throws the not-found sentence as a 400, not a 404.
    if (matches(message, "not found")) return { ...base, kind: "wallet_not_found" };
  }

  return { ...base, kind: "generic" };
}

/**
 * Whether the kind keeps the dialog on the review step. Nothing was written and the form is still valid, so
 * sending the operator to a dead-end result screen would throw away input that is about to be reused.
 */
export function staysOnReview(kind: AdjustErrorKind): boolean {
  return kind === "rate_limited";
}
